import { ZoomIn, ZoomOut } from "lucide-react";
import { useRef, useState } from "react";
import { Button } from "./components/ui/button";
import { photoCrop } from "./lib/photo-crop";
import type { PhotoCropPosition } from "./lib/photo-crop";

type Props = {
  src: string;
  fit?: "cover" | "contain";
  shape?: "circle" | "square";
  disabled?: boolean;
  onApply: (image: string) => void;
  onCancel: () => void;
  labels: {
    preview: string;
    zoom: string;
    zoomIn: string;
    zoomOut: string;
    cancel: string;
    reset: string;
    apply: string;
    error: string;
    loading: string;
  };
};

export function PhotoCropper(props: Props) {
  return <CropSession key={`${props.src}:${props.fit ?? "cover"}`} {...props} />;
}

function CropSession({
  src,
  onApply,
  onCancel,
  labels,
  fit = "cover",
  shape = "circle",
  disabled = false,
}: Props) {
  const image = useRef<HTMLImageElement>(null);
  const drag = useRef<{
    pointerId: number;
    clientX: number;
    clientY: number;
    x: number;
    y: number;
  } | null>(null);
  const applying = useRef(false);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const [position, setPosition] = useState<PhotoCropPosition>({ zoom: 1, x: 0, y: 0 });
  const [error, setError] = useState(false);
  const crop = dimensions ? photoCrop(dimensions.width, dimensions.height, position, fit) : null;

  function adjust(next: PhotoCropPosition) {
    if (!dimensions) return;
    const { zoom, x, y } = photoCrop(dimensions.width, dimensions.height, next, fit);
    setPosition({ zoom, x, y });
  }
  function apply() {
    if (disabled || !crop || !image.current || applying.current) return;
    applying.current = true;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = 256;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Canvas unavailable");
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      // Intersect the source with the image; contain mode keeps transparent margins.
      const sourceLeft = Math.max(0, crop.left);
      const sourceTop = Math.max(0, crop.top);
      const sourceWidth = Math.min(image.current.naturalWidth, crop.left + crop.size) - sourceLeft;
      const sourceHeight = Math.min(image.current.naturalHeight, crop.top + crop.size) - sourceTop;
      context.drawImage(
        image.current,
        sourceLeft,
        sourceTop,
        sourceWidth,
        sourceHeight,
        ((sourceLeft - crop.left) / crop.size) * 256,
        ((sourceTop - crop.top) / crop.size) * 256,
        (sourceWidth / crop.size) * 256,
        (sourceHeight / crop.size) * 256,
      );
      onApply(canvas.toDataURL("image/webp", 0.9));
    } catch {
      setError(true);
    } finally {
      applying.current = false;
    }
  }

  return (
    <div className="space-y-5" data-testid="photo-cropper">
      <div
        role="group"
        tabIndex={0}
        aria-label={labels.preview}
        data-testid="photo-crop-preview"
        className={`relative mx-auto aspect-square w-full max-w-72 touch-none overflow-hidden ${shape === "square" ? "rounded-xl" : "rounded-full"} border-4 border-border bg-muted outline-none select-none focus-visible:ring-2 focus-visible:ring-ring cursor-grab active:cursor-grabbing`}
        onPointerDown={(event) => {
          if (disabled || !crop || event.button !== 0) return;
          event.preventDefault();
          event.currentTarget.focus();
          event.currentTarget.setPointerCapture(event.pointerId);
          drag.current = {
            pointerId: event.pointerId,
            clientX: event.clientX,
            clientY: event.clientY,
            x: crop.x,
            y: crop.y,
          };
        }}
        onPointerMove={(event) => {
          const current = drag.current;
          if (disabled || !crop || current?.pointerId !== event.pointerId) return;
          const side = event.currentTarget.clientWidth;
          if (!side) return;
          adjust({
            zoom: crop.zoom,
            x: current.x + (event.clientX - current.clientX) / side,
            y: current.y + (event.clientY - current.clientY) / side,
          });
        }}
        onPointerUp={(event) => {
          if (drag.current?.pointerId !== event.pointerId) return;
          drag.current = null;
          if (event.currentTarget.hasPointerCapture(event.pointerId))
            event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
        onLostPointerCapture={() => {
          drag.current = null;
        }}
        onKeyDown={(event) => {
          if (disabled || !crop) return;
          const delta = event.shiftKey ? 0.1 : 0.02;
          const movement = {
            ArrowLeft: [-delta, 0],
            ArrowRight: [delta, 0],
            ArrowUp: [0, -delta],
            ArrowDown: [0, delta],
          }[event.key];
          if (movement) {
            event.preventDefault();
            adjust({ zoom: crop.zoom, x: crop.x + movement[0]!, y: crop.y + movement[1]! });
          } else if (event.key === "Home") {
            event.preventDefault();
            adjust({ zoom: 1, x: 0, y: 0 });
          }
        }}
      >
        <img
          ref={image}
          src={src}
          alt=""
          draggable={false}
          className="pointer-events-none absolute max-w-none select-none"
          style={
            crop && dimensions
              ? {
                  width: `${(dimensions.width / crop.size) * 100}%`,
                  height: `${(dimensions.height / crop.size) * 100}%`,
                  left: `${50 - (dimensions.width / crop.size) * 50 + crop.x * 100}%`,
                  top: `${50 - (dimensions.height / crop.size) * 50 + crop.y * 100}%`,
                }
              : { width: "100%", height: "100%", objectFit: fit }
          }
          onLoad={(event) => {
            const { naturalWidth: width, naturalHeight: height } = event.currentTarget;
            if (!width || !height) {
              setError(true);
              return;
            }
            setDimensions({ width, height });
            setError(false);
          }}
          onError={() => setError(true)}
        />
        {!crop && !error ? (
          <span
            role="status"
            className="absolute inset-0 grid place-items-center text-base text-muted-foreground"
          >
            {labels.loading}
          </span>
        ) : null}
      </div>
      <div className="mx-auto flex max-w-80 items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled || !crop}
          aria-label={labels.zoomOut}
          onClick={() => adjust({ ...position, zoom: position.zoom - 0.1 })}
        >
          <ZoomOut className="size-5" />
        </Button>
        <input
          type="range"
          min={fit === "contain" ? 0.5 : 1}
          max="4"
          step="0.01"
          value={position.zoom}
          disabled={disabled || !crop}
          aria-label={labels.zoom}
          aria-valuetext={`${Math.round(position.zoom * 100)}%`}
          onChange={(event) => adjust({ ...position, zoom: Number(event.target.value) })}
          className="h-11 min-w-0 flex-1 accent-primary"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled || !crop}
          aria-label={labels.zoomIn}
          onClick={() => adjust({ ...position, zoom: position.zoom + 0.1 })}
        >
          <ZoomIn className="size-5" />
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-base text-destructive">
          {labels.error}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button type="button" variant="secondary" disabled={disabled} onClick={onCancel}>
          {labels.cancel}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={disabled || !crop}
          onClick={() => {
            adjust({ zoom: 1, x: 0, y: 0 });
            setError(false);
          }}
        >
          {labels.reset}
        </Button>
        <Button type="button" disabled={disabled || !crop} onClick={apply}>
          {labels.apply}
        </Button>
      </div>
    </div>
  );
}
